'use client';

import { useEffect, useRef, useState } from 'react';

/**
 * Client component that adds a placeholder comment to identify SSR-rendered pages
 * This placeholder gets replaced during static export by the export-static.js script
 *
 * The comment is added as a data attribute on a hidden element that the script can find and replace
 *
 * IMPORTANT: This component checks if a static rebuild comment already exists in the DOM.
 * If it does, it means the page was loaded from static HTML (S3), and we should NOT
 * replace it with an SSR comment during client-side navigation.
 */
export default function SSRComment() {
  const [shouldRender, setShouldRender] = useState(true);
  const hasCheckedRef = useRef(false);

  useEffect(() => {
    // Only check once on mount
    if (hasCheckedRef.current) return;
    hasCheckedRef.current = true;

    // Check if there's already a static rebuild comment in the DOM
    // This means the page was loaded from static HTML (S3/CloudFront)
    // Check for any STATIC REBUILD pattern (not just V2, as different routes use different formats)
    const existingComment = document.querySelector('[data-ssr-comment*="STATIC REBUILD"]');

    if (existingComment) {
      // Page was loaded from static HTML - don't render SSR comment
      // This prevents the comment from changing during client-side navigation
      setShouldRender(false);
    }
  }, []);

  // Don't render if we detected a static rebuild comment
  if (!shouldRender) {
    return null;
  }

  // Use a static string (no Date) so server and client render the same and avoid hydration mismatch.
  // See https://react.dev/errors/418
  return (
    <div
      data-ssr-comment="SSR BUILD - SERVED FROM NEXT.JS SERVER (ALB) - If you see this message, the page is being served from the Next.js SSR server NOT from S3 static files."
      style={{ display: 'none' }}
      suppressHydrationWarning={true}
    />
  );
}
